<script setup lang="ts">
import { computed, ref } from "vue";
import InteractiveTopics from "~/components/InteractiveTopics.vue";
import PageContainer from "~/components/ui/PageContainer.vue";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";
import { pythonInteractiveCards } from "~/features/python/data/chapter2-interactive";
import { pythonInteractiveRouteByConcept } from "~/features/python/data/chapter2-interactive";

type PythonShortTopic =
  | "variables"
  | "input-output"
  | "operators"
  | "if-else"
  | "loops"
  | "functions"
  | "lists";

type PythonShortQuestion = {
  question: string;
  answer: string;
  topic: PythonShortTopic;
  important?: boolean;
};

const topicOrder: PythonShortTopic[] = [
  "variables",
  "input-output",
  "operators",
  "if-else",
  "loops",
  "functions",
  "lists",
];

const topicLabels: Record<PythonShortTopic, string> = {
  variables: "Variables",
  "input-output": "Input / Output",
  operators: "Operators",
  "if-else": "If-Else",
  loops: "Loops",
  functions: "Functions",
  lists: "Lists",
};

const topicLinks: Record<PythonShortTopic, string> = {
  variables: pythonInteractiveRouteByConcept.variables,
  "input-output": pythonInteractiveRouteByConcept["input-output"],
  operators: pythonInteractiveRouteByConcept.operators,
  "if-else": pythonInteractiveRouteByConcept["if-else"],
  loops: pythonInteractiveRouteByConcept.loops,
  functions: pythonInteractiveRouteByConcept.functions,
  lists: "/11th/computer/data-structures/",
};

const questions: PythonShortQuestion[] = [
  {
    topic: "variables",
    question: "Variable kya hota hai?",
    answer: "Variable aik naam wala storage hota hai jahan program temporary value rakh leta hai.",
    important: true,
  },
  {
    topic: "variables",
    question: "Python me valid variable name ka basic rule kya hai?",
    answer: "Variable name letter ya underscore se start hota hai. Space aur keyword allowed nahi hotay.",
  },
  {
    topic: "input-output",
    question: "Input kya hota hai?",
    answer: "Input wo value hoti hai jo user program ko deta hai.",
  },
  {
    topic: "input-output",
    question: "Output kya hota hai?",
    answer: "Output wo result hota hai jo program screen par show karta hai.",
  },
  {
    topic: "operators",
    question: "Operator kya hota hai?",
    answer: "Operator wo symbol hota hai jo calculation, comparison ya logic perform karta hai.",
  },
  {
    topic: "operators",
    question: "Comparison operator ka result kya hota hai?",
    answer: "Comparison operator ka result `True` ya `False` hota hai.",
  },
  {
    topic: "if-else",
    question: "If-else kya karta hai?",
    answer: "If-else condition check karke decision leta hai.",
  },
  {
    topic: "if-else",
    question: "Else block kab chalta hai?",
    answer: "Else block tab chalta hai jab if wali condition false ho.",
  },
  {
    topic: "loops",
    question: "Loop kya karta hai?",
    answer: "Loop ek kaam ko bar bar repeat karta hai.",
    important: true,
  },
  {
    topic: "loops",
    question: "For loop kab use hota hai?",
    answer: "For loop tab use hota hai jab repeat count ya items pehle se clear hon.",
  },
  {
    topic: "loops",
    question: "While loop kab rukti hai?",
    answer: "While loop tab rukti hai jab condition false ho jaye.",
  },
  {
    topic: "functions",
    question: "Function kya hota hai?",
    answer: "Function code ka reusable block hota hai jo aik specific kaam karta hai.",
    important: true,
  },
  {
    topic: "functions",
    question: "Parameter kya hota hai?",
    answer: "Parameter wo value hoti hai jo function ke andar kaam karne ke liye di jati hai.",
  },
  {
    topic: "functions",
    question: "Return ka kya kaam hai?",
    answer: "Return function ke result ko wapas bhejta hai.",
  },
  {
    topic: "lists",
    question: "List kya hoti hai?",
    answer: "List aik structure hoti hai jisme multiple values aik jagah store ki jati hain.",
  },
  {
    topic: "lists",
    question: "List ka item kaise access hota hai?",
    answer: "List ke item ko index ke zariye square brackets me access kiya jata hai.",
  },
];

const grouped = computed(() => {
  const map: Partial<Record<PythonShortTopic, PythonShortQuestion[]>> = {};

  questions.forEach((question) => {
    if (!map[question.topic]) {
      map[question.topic] = [];
    }

    map[question.topic]?.push(question);
  });

  return map;
});

const importantQuestions = computed(() => questions.filter((question) => question.important));

const openQuestion = ref<string | null>(null);
const showAllAnswers = ref(false);

function toggle(id: string) {
  openQuestion.value = openQuestion.value === id ? null : id;
}

function scrollToTopic(topic: PythonShortTopic | "all") {
  if (topic === "all") {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }

  document.getElementById(topic)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function isOpen(id: string) {
  return showAllAnswers.value || openQuestion.value === id;
}

usePageSeo({
  title: "Chapter 2 Python Short Questions with Answers | Ustaad",
  description: "Important short questions for 11th class Python with simple answers. Topic-wise revision for variables, loops, functions and lists.",
  keywords: "11th class python short questions with answers, chapter 2 short questions, python revision questions",
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "Ch 2 Python", item: "/11th/computer/python/" },
    { name: "Short Questions", item: "/11th/computer/python/short-questions/" },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: "Chapter 2 Python Short Questions",
    items: [
      { name: "Chapter 2 Python", path: "/11th/computer/python/" },
      { name: "Chapter 2 Python MCQs", path: "/11th/computer/python/mcqs/" },
      { name: "Chapter 2 Python Long Questions", path: "/11th/computer/python/long-questions/" },
      { name: "Chapter 2 Python Exercise", path: "/11th/computer/python/exercise/" },
      { name: "Important Questions", path: "/11th/computer/important-questions/" },
    ],
  }),
});
</script>

<template>
  <PageContainer>
    <div class="space-y-8">
      <section class="space-y-4 rounded-3xl bg-slate-950 p-6 text-white">
        <h1 class="text-2xl font-bold tracking-tight">Chapter 2 Python Short Questions</h1>
        <p class="text-sm leading-7 text-slate-200">Important exam questions with simple answers</p>

        <div class="flex snap-x snap-mandatory gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none]">
          <button type="button" class="snap-start shrink-0 rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-900" @click="scrollToTopic('all')">
            All
          </button>
          <button
            v-for="topic in topicOrder"
            :key="topic"
            type="button"
            class="snap-start shrink-0 rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-900"
            @click="scrollToTopic(topic)"
          >
            {{ topicLabels[topic] }}
          </button>
        </div>
      </section>

      <section class="rounded-3xl border border-amber-200 bg-amber-50 p-5">
        <div class="flex items-center justify-between gap-3">
          <h2 class="text-lg font-semibold text-slate-900">Most Important Questions</h2>
          <button
            type="button"
            class="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-900 ring-1 ring-amber-200"
            @click="showAllAnswers = !showAllAnswers"
          >
            {{ showAllAnswers ? "Hide Answers" : "Show Answers" }}
          </button>
        </div>

        <div class="mt-4 space-y-3">
          <div
            v-for="(question, index) in importantQuestions"
            :key="`important-${index}`"
            class="rounded-2xl bg-white p-4 ring-1 ring-amber-100"
          >
            <p class="text-sm font-semibold text-slate-900">{{ question.question }}</p>
            <button type="button" class="mt-3 text-sm font-medium text-sky-700 hover:text-sky-900" @click="toggle(`important-${index}`)">
              {{ isOpen(`important-${index}`) ? "Hide Answer" : "Show Answer" }}
            </button>
            <p v-if="isOpen(`important-${index}`)" class="mt-2 text-sm leading-7 text-slate-700">{{ question.answer }}</p>
          </div>
        </div>
      </section>

      <InteractiveTopics
        eyebrow="Python Chapter 2"
        title="Python Topic Practice"
        intro="Short questions se pehle ya baad me topic-wise revision karni ho to yeh pages khol kar concept fresh karein."
        :cards="pythonInteractiveCards"
      />

      <section
        v-for="topic in topicOrder"
        :id="topic"
        :key="topic"
        class="space-y-4 rounded-3xl border border-slate-200 bg-white p-5 scroll-mt-24"
      >
        <template v-if="grouped[topic]?.length">
          <div class="space-y-1">
            <h2 class="text-lg font-semibold text-slate-900">{{ topicLabels[topic] }}</h2>
            <p class="text-sm text-slate-600">{{ grouped[topic]?.length }} questions</p>
          </div>

          <div class="space-y-3">
            <div
              v-for="(question, index) in grouped[topic]"
              :key="`${topic}-${index}`"
              class="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200"
            >
              <p class="text-sm font-medium text-slate-900">{{ question.question }}</p>
              <button type="button" class="mt-3 text-sm font-medium text-sky-700 hover:text-sky-900" @click="toggle(`${topic}-${index}`)">
                {{ isOpen(`${topic}-${index}`) ? "Hide Answer" : "Show Answer" }}
              </button>
              <p v-if="isOpen(`${topic}-${index}`)" class="mt-2 text-sm leading-7 text-slate-700">{{ question.answer }}</p>
            </div>
          </div>

          <div class="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <NuxtLink :to="topicLinks[topic]" class="text-sm font-medium text-sky-700 hover:text-sky-900">
              {{ topic === "loops" ? "Loops revise karein ->" : topic === "functions" ? "Functions revise karein ->" : `${topicLabels[topic]} revise karein ->` }}
            </NuxtLink>
          </div>
        </template>
      </section>

      <section class="rounded-3xl border border-rose-200 bg-rose-50 p-5">
        <h2 class="text-lg font-semibold text-slate-900">Common Mistakes</h2>
        <div class="mt-4 space-y-3 text-sm text-slate-700">
          <p>ratna bina example samjhe</p>
          <p>keywords miss kar dena</p>
          <p>mixed topics ko aik hi answer me confuse kar dena</p>
        </div>
      </section>
    </div>
  </PageContainer>
</template>

